import {useCallback, useRef} from 'react'

import {omit} from '../../../../utils/omit'
import type {Repository} from '../../../api/common-contracts'
import {apiApplyTemplate} from '../../../api/memex/api-apply-template'
import type {ApplyTemplateRequest} from '../../../api/memex/contracts'
import {apiRepositoryBulkAddItems} from '../../../api/memex-items/api-repository-bulk-add-items'
import {DefaultOmitPropertiesForView} from '../../../api/view/contracts'
import {apiCreateWorkflow} from '../../../api/workflows/api-create-workflow'
import {MemexWorkflowContentType} from '../../../api/workflows/contracts'
import type {SelectedTemplate} from '../../../components/template-dialog/hooks/use-template-link'
import useToasts from '../../../components/toasts/use-toasts'
import {getViewTypeParamFromViewType} from '../../../helpers/view-type'
import {isAutoAddWorkflow} from '../../../helpers/workflow-utilities'
import {useApiRequest} from '../../../hooks/use-api-request'
import {useUpdateViewWithoutSettingStates} from '../../../hooks/use-view-apis'
import {useViews} from '../../../hooks/use-views'
import {useBulkAddNotifications} from '../../../state-providers/bulk-add-notifications/bulk-add-notifications-state-provider'
import {useSingleDataRefresh} from '../../../state-providers/data-refresh/use-single-data-refresh'
import {useUpdateMemexSettings} from '../../../state-providers/memex/use-update-memex-settings'
import {useWorkflows} from '../../../state-providers/workflows/use-workflows'
import {BulkCopyResources} from '../../../strings'

const INITIAL_BULK_IMPORT_COUNT = 500

export type ApplyTemplateArgs = {
  title: string
  template: SelectedTemplate
  rollback?: () => void
  autoAddEnabledRepository?: Repository
  autoAddEntities?: Array<'issues' | 'prs'>
}

export function useApplyTemplate() {
  const handleDataRefresh = useSingleDataRefresh()
  const {addToast} = useToasts()
  const copyingDraftsAsync = useRef(false)

  const {viewsMap} = useViews()
  const updateView = useUpdateViewWithoutSettingStates()
  const workflows = useWorkflows()
  const {notifyBulkAddStarted} = useBulkAddNotifications()

  const {updateSettings} = useUpdateMemexSettings()
  const applyTemplateRequest = useApiRequest({
    request: async (requestData: ApplyTemplateRequest) => {
      const res = await apiApplyTemplate(requestData)
      if (res.copyingDraftsAsync) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({message: BulkCopyResources.copyingDraftsMessage, type: 'default', keepAlive: false})
        copyingDraftsAsync.current = true
      }
    },
    rollback: (rollbackAction?: () => void) => {
      rollbackAction?.()
    },
    showErrorToast: true,
  })

  const applyTemplate = useCallback(
    async function ({template, title, rollback, autoAddEnabledRepository, autoAddEntities}: ApplyTemplateArgs) {
      const actions: Array<Promise<unknown>> = [
        updateSettings({title, defaultIssueCreateRepository: autoAddEnabledRepository}),
      ]
      const contentTypes: Array<MemexWorkflowContentType> = []
      const queryEntities = []
      for (const entity of autoAddEntities ?? []) {
        if (entity === 'issues') {
          contentTypes.push(MemexWorkflowContentType.Issue)
          queryEntities.push('issue')
        } else if (entity === 'prs') {
          contentTypes.push(MemexWorkflowContentType.PullRequest)
          queryEntities.push('pr')
        }
      }
      if (template.type === 'layout') {
        const view = viewsMap[1]
        if (view) {
          actions.push(
            updateView.perform({
              viewNumber: view.number,
              view: omit(
                {
                  ...view.serverViewState,
                  layout: getViewTypeParamFromViewType(template.viewType),
                },
                DefaultOmitPropertiesForView,
              ),
            }),
          )
        }

        if (autoAddEnabledRepository && autoAddEntities?.length) {
          const workflow = workflows.workflows.find(isAutoAddWorkflow)
          const getAction = workflow?.actions[0]
          const addAction = workflow?.actions[1]
          if (workflow && getAction && addAction) {
            const updatedGetAction = {
              ...getAction,
              arguments: {
                repositoryId: autoAddEnabledRepository?.id,
                query: `is:${queryEntities.join(',')} is:open`,
              },
            }
            const updatedAddAction = {
              ...addAction,
              arguments: {
                repositoryId: autoAddEnabledRepository?.id,
              },
            }
            actions.push(
              apiCreateWorkflow({
                workflow: {
                  name: workflow.name,
                  enabled: true,
                  contentTypes,
                  triggerType: workflow.triggerType,
                  actions: [updatedGetAction, updatedAddAction],
                },
              }),
            )
          }
        }
      } else {
        const templateToApply =
          template.type === 'custom' ? String(template.template.projectNumber) : template.template.id
        const autoEnableArgs =
          template.type === 'custom' ? {} : {autoAddEnabledRepositoryId: autoAddEnabledRepository?.id, autoAddEntities}
        actions.push(
          applyTemplateRequest.perform(
            {
              template: templateToApply,
              ...autoEnableArgs,
            },
            rollback,
          ),
        )
      }
      if (autoAddEnabledRepository && autoAddEntities?.length && contentTypes.length) {
        actions.push(
          apiRepositoryBulkAddItems({
            repositoryId: autoAddEnabledRepository.id,
            count: INITIAL_BULK_IMPORT_COUNT,
            contentTypes: queryEntities,
          }).then(([success, requestId]) => {
            if (success && requestId) {
              notifyBulkAddStarted(requestId)
            }
          }),
        )
      }

      await Promise.all(actions)

      // Refresh the project with all new data
      await handleDataRefresh()
    },
    [applyTemplateRequest, handleDataRefresh, updateSettings, updateView, viewsMap, workflows, notifyBulkAddStarted],
  )

  const isApplyingTemplate = applyTemplateRequest.status.current.status === 'loading'

  return {applyTemplate, isApplyingTemplate, copyingDraftsAsync}
}
